import { useState } from 'react';
import { debounce } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { FormSubmitHandler, reset } from 'redux-form';
import { TextKey } from '@mews-commander/enums';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { LocalizedSectionTitle, FieldsContainer } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { InputValue } from '@optimus-web/common';
import {
    commanderForm,
    DropzoneField,
    InputField,
    maxInputTextLength,
    maxLongInputTextLength,
    presence,
    TextareaField,
    validation,
} from '@mews-commander/legacy-form';
import {
    MINIMAL_SEARCH_TOKEN_LENGTH,
    SUPPORTED_TICKET_ATTACHMENT_FILE_EXTENSION,
    TICKET_DESCRIPTION_ROW_COUNT,
} from '../constants';
import { submitSupportTicket } from '../actions';
import { sanitazeSearchValue } from '../utils';
import { TicketFormValues } from '../types';
import { ArticleSuggestion } from './ArticleSuggestion';

interface DispatchProps {
    onSubmit: FormSubmitHandler<TicketFormValues>;
}

const FORM_NAME = 'helpCenterTicketForm';

const constraints = {
    title: { presence, length: maxInputTextLength },
    description: { presence, length: maxLongInputTextLength },
};

const TicketFormFields = () => {
    const [titleValue, setTitleValue] = useState('');

    const debounceInputValueChange = debounce(
        1000,
        (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: InputValue) => {
            const sanitizedValue = sanitazeSearchValue(value.toString());
            setTitleValue(sanitizedValue);
        }
    );

    return (
        <Stack spacing="200" vertical>
            <LocalizedSectionTitle textKey={TextKey.CanHelpTitle} />
            <FieldsContainer>
                <InputField {...titleProps} onChange={debounceInputValueChange} />
                {titleValue.length >= MINIMAL_SEARCH_TOKEN_LENGTH && <ArticleSuggestion searchToken={titleValue} />}
                <TextareaField {...descriptionProps} />
                <DropzoneField {...fileProps} />
            </FieldsContainer>
        </Stack>
    );
};

export const titleProps = {
    label: <LocalizedText textKey={TextKey.Subject} />,
    name: 'title',
    required: true,
    placeholder: <LocalizedText textKey={TextKey.HelpCenterTicketFormTitlePlaceholder} />,
};

const descriptionProps = {
    label: <LocalizedText textKey={TextKey.Message} />,
    name: 'description',
    required: true,
    placeholder: <LocalizedText textKey={TextKey.HelpCenterTicketFormDescriptionPlaceholder} />,
    rows: TICKET_DESCRIPTION_ROW_COUNT,
};

export const fileProps = {
    label: <LocalizedText textKey={TextKey.FilePlural} />,
    name: 'files',
    accept: SUPPORTED_TICKET_ATTACHMENT_FILE_EXTENSION,
    multiple: true,
};

export default pipe(
    TicketFormFields,
    commanderForm({
        destroyOnUnmount: false,
        submitLabel: <LocalizedText textKey={TextKey.Send} />,
        validate: validation(constraints),
        submitButtonProps: { variant: 'primary' },
        form: FORM_NAME,
    }),
    connect<{}, DispatchProps>(undefined, (dispatch: CommanderThunkDispatch) => ({
        onSubmit: async (values) => {
            await dispatch(submitSupportTicket(values));

            dispatch(reset(FORM_NAME));
            showSuccessAlert({ description: lt('HelpCenterTicketFormSuccessMessage') });
        },
    }))
);
